import { useEffect, useState } from "react";

const GA_MEASUREMENT_ID = "G-XXXXXXXXXX";
const CONSENT_COOKIE = "foxaya_cookie_consent";
const CONSENT_MAX_AGE = 60 * 60 * 24 * 180; // 6 mois

function CookieConsent() {
  const [consent, setConsent] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);

  useEffect(() => {
    const storedConsent = readConsent();
    setConsent(storedConsent);

    if (storedConsent === "accepted") {
      loadGoogleAnalytics();
      setBannerOpen(false);
    } else if (storedConsent === "refused") {
      disableGoogleAnalytics();
      setBannerOpen(false);
    } else {
      disableGoogleAnalytics();
      setBannerOpen(true);
    }
  }, []);

  function readConsent() {
    if (typeof document === "undefined") return null;

    const row = document.cookie
      .split("; ")
      .find((item) => item.startsWith(`${CONSENT_COOKIE}=`));

    return row ? decodeURIComponent(row.split("=")[1]) : null;
  }

  function writeConsent(value) {
    document.cookie =
      `${CONSENT_COOKIE}=${encodeURIComponent(value)}` +
      `; Max-Age=${CONSENT_MAX_AGE}; Path=/; SameSite=Lax; Secure`;
  }

  function deleteCookie(name) {
    ["", ".foxaya.fr"].forEach((domain) => {
      document.cookie =
        `${name}=; Max-Age=0; Path=/; SameSite=Lax` +
        (domain ? `; Domain=${domain}` : "");
    });
  }

  function removeAnalyticsCookies() {
    document.cookie.split(";").forEach((part) => {
      const name = part.split("=")[0].trim();

      if (
        name === "_ga" ||
        name.startsWith("_ga_") ||
        name === "_gid" ||
        name === "_gat"
      ) {
        deleteCookie(name);
      }
    });
  }

  function analyticsConfigured() {
    return (
      /^G-[A-Z0-9]+$/i.test(GA_MEASUREMENT_ID) &&
      GA_MEASUREMENT_ID !== "G-XXXXXXXXXX"
    );
  }

  function loadGoogleAnalytics() {
    if (
      typeof window === "undefined" ||
      !analyticsConfigured() ||
      window.__foxayaGaLoaded
    ) {
      return;
    }

    window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function gtag() {
        window.dataLayer.push(arguments);
      };

    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID, {
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });

    const script = document.createElement("script");
    script.async = true;
    script.src =
      `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
        GA_MEASUREMENT_ID
      )}`;
    document.head.appendChild(script);

    window.__foxayaGaLoaded = true;
  }

  function disableGoogleAnalytics() {
    if (typeof window === "undefined") return;

    if (analyticsConfigured()) {
      window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
    }

    removeAnalyticsCookies();
  }

  function acceptAll() {
    writeConsent("accepted");
    setConsent("accepted");
    loadGoogleAnalytics();
    setBannerOpen(false);
  }

  function refuseOptional() {
    writeConsent("refused");
    setConsent("refused");
    disableGoogleAnalytics();
    setBannerOpen(false);
  }

  return (
    <>
      <style>{`
        .foxaya-cookie-banner {
          position: fixed;
          z-index: 200000;
          left: 16px;
          right: 16px;
          bottom: 16px;
          width: min(760px, calc(100% - 32px));
          margin: 0 auto;
          padding: 20px;
          border: 1px solid rgba(3, 43, 56, 0.14);
          border-radius: 20px;
          background: #eaf8fb;
          color: #063746;
          box-shadow: 0 24px 70px rgba(3, 43, 56, 0.28);
          font-family: "Montserrat", Arial, sans-serif;
        }

        .foxaya-cookie-banner h2 {
          margin: 0 0 8px;
          color: #063746;
          font-size: 18px;
          line-height: 1.25;
          font-weight: 800;
        }

        .foxaya-cookie-banner p {
          margin: 0;
          color: #365d67;
          font-size: 13px;
          line-height: 1.65;
        }

        .foxaya-cookie-more {
          display: inline-flex;
          margin-top: 12px;
          padding: 0;
          border: 0;
          background: transparent;
          color: #087f8c;
          font: inherit;
          font-size: 12px;
          font-weight: 700;
          text-decoration: underline;
          text-underline-offset: 3px;
          cursor: pointer;
        }

        .foxaya-cookie-details {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid rgba(3, 43, 56, 0.12);
        }

        .foxaya-cookie-details p {
          margin: 0 0 9px;
          color: #496b73;
          font-size: 12px;
        }

        .foxaya-cookie-details p:last-child {
          margin-bottom: 0;
        }

        .foxaya-cookie-details strong {
          color: #063746;
        }

        .foxaya-cookie-actions {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          margin-top: 16px;
        }

        .foxaya-cookie-choice {
          min-height: 46px;
          width: 100%;
          padding: 0 16px;
          border: 1px solid #063746;
          border-radius: 12px;
          font-family: inherit;
          font-size: 13px;
          font-weight: 800;
          cursor: pointer;
          transition: background .2s, color .2s, border-color .2s;
        }

        .foxaya-cookie-refuse {
          background: transparent;
          color: #063746;
        }

        .foxaya-cookie-refuse:hover,
        .foxaya-cookie-refuse:focus-visible {
          background: #063746;
          color: #fff;
        }

        .foxaya-cookie-accept {
          background: #063746;
          color: #fff;
        }

        .foxaya-cookie-accept:hover,
        .foxaya-cookie-accept:focus-visible {
          border-color: #087f8c;
          background: #087f8c;
        }

        .foxaya-cookie-manage {
          position: fixed;
          z-index: 199999;
          left: 14px;
          bottom: 14px;
          padding: 8px 11px;
          border: 1px solid rgba(123, 230, 237, .24);
          border-radius: 999px;
          background: rgba(0, 28, 43, .92);
          color: #d8e7ea;
          font-family: "Montserrat", Arial, sans-serif;
          font-size: 10px;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 8px 24px rgba(0, 0, 0, .16);
        }

        .foxaya-cookie-choice:focus-visible,
        .foxaya-cookie-more:focus-visible,
        .foxaya-cookie-manage:focus-visible {
          outline: 3px solid #9cd14d;
          outline-offset: 3px;
        }

        @media (max-width: 560px) {
          .foxaya-cookie-banner {
            left: 10px;
            right: 10px;
            bottom: 10px;
            width: calc(100% - 20px);
            padding: 16px;
            border-radius: 16px;
          }

          .foxaya-cookie-banner h2 {
            font-size: 17px;
          }

          .foxaya-cookie-actions {
            grid-template-columns: 1fr;
          }

          .foxaya-cookie-manage {
            left: 10px;
            bottom: 10px;
          }
        }
      `}</style>

      {bannerOpen && (
        <section
          className="foxaya-cookie-banner"
          role="dialog"
          aria-modal="false"
          aria-labelledby="foxaya-cookie-title"
          aria-describedby="foxaya-cookie-description"
        >
          <h2 id="foxaya-cookie-title">Vos choix concernant les cookies</h2>

          <p id="foxaya-cookie-description">
            Foxaya utilise des cookies essentiels au fonctionnement du site.
            Ils sont toujours actifs. Avec votre accord, Google Analytics peut
            aussi être utilisé pour mesurer l’audience et améliorer le site.
          </p>

          <button
            type="button"
            className="foxaya-cookie-more"
            aria-expanded={detailsOpen}
            onClick={() => setDetailsOpen((open) => !open)}
          >
            Plus d’informations
          </button>

          {detailsOpen && (
            <div className="foxaya-cookie-details">
              <p>
                <strong>Cookies essentiels — toujours actifs.</strong>{" "}
                Nécessaires au fonctionnement du site et à la mémorisation de
                votre choix de confidentialité.
              </p>
              <p>
                <strong>Mesure d’audience — facultative.</strong>{" "}
                Google Analytics n’est chargé qu’après votre accord.
              </p>
              <p>
                <strong>Votre choix.</strong>{" "}
                Il est conservé 6 mois et peut être modifié à tout moment.
              </p>
            </div>
          )}

          <div className="foxaya-cookie-actions">
            <button
              type="button"
              className="foxaya-cookie-choice foxaya-cookie-refuse"
              onClick={refuseOptional}
            >
              Tout refuser
            </button>

            <button
              type="button"
              className="foxaya-cookie-choice foxaya-cookie-accept"
              onClick={acceptAll}
            >
              Tout accepter
            </button>
          </div>
        </section>
      )}

      {!bannerOpen && consent && (
        <button
          type="button"
          className="foxaya-cookie-manage"
          onClick={() => setBannerOpen(true)}
        >
          Gérer mes cookies
        </button>
      )}
    </>
  );
}


// ⚠️ Mascotte Renard (pose "sort du bord de l'écran") encodée en base64 pour
// un affichage garanti, sans dépendre d'un fichier hébergé séparément.
const FOX_SIDE_IMG_URL = "data:image/webp;base64,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";

const DOTS = [
  [120,80,3],[1080,110,1.5],[1140,600,4],[60,650,2],[600,30,1],
  [300,450,3.5],[900,350,1.5],[450,200,2.5],[750,500,1],[200,300,4],
  [1000,450,2],[350,150,1.5],[650,650,3],[850,150,2],[500,550,1],
  [150,500,3.5],[1050,250,1.5],[700,100,2.5],[250,700,1],[1000,700,2],[400,700,3],
];

export default function HeroPage({ onStart }) {
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" ? window.innerWidth < 768 : false);
  const [showPrototypeNotice, setShowPrototypeNotice] = useState(true);
  const [showFox, setShowFox] = useState(false);
  const [showFoxBubble, setShowFoxBubble] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [mode, setMode] = useState('signup'); // 'signup' | 'login'
  const [form, setForm] = useState({ prenom: '', email: '', secteur: '' });
  const [loginEmail, setLoginEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loginError, setLoginError] = useState('');

  const FOX_BUBBLE_SEEN_KEY = 'foxaya_fox_bubble_seen:hero';

  const SECTEURS = [
    { value: '🏭 Industrie / Fabrication', label: 'Industrie / Fabrication', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00A5B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 20h20M4 20V10l6-4v4l6-4v14"/><path d="M12 20v-6h4v6"/></svg> },
    { value: '🍞 Alimentation / Restauration', label: 'Alimentation / Restauration', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00A5B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg> },
    { value: '🏗️ BTP / Construction', label: 'BTP / Construction', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00A5B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="15" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg> },
    { value: '💻 Tech / Numérique', label: 'Tech / Numérique', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00A5B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg> },
    { value: '🛍️ Commerce / Retail', label: 'Commerce / Retail', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00A5B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg> },
    { value: '🚚 Transport / Logistique', label: 'Transport / Logistique', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00A5B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg> },
    { value: '🌾 Agriculture', label: 'Agriculture', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00A5B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22V12"/><path d="M5 12C5 7 12 2 12 2s7 5 7 10a7 7 0 0 1-14 0z"/></svg> },
    { value: '💆 Services à la personne', label: 'Services à la personne', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00A5B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
    { value: '🏥 Santé / Bien-être', label: 'Santé / Bien-être', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00A5B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg> },
    { value: '🎓 Éducation / Formation', label: 'Éducation / Formation', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00A5B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg> },
  ];

  const API = 'https://foxaya.fr/blog/api';

  const handleSignup = async () => {
    if (!form.prenom || !form.email || !form.secteur || !form.password) return;
    try {
      const res = await fetch(`${API}/inscription.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('foxaya_user', JSON.stringify({ ...data.user, createdAt: Date.now() }));
        setSubmitted(true);
        setTimeout(() => {
          setShowSignup(false);
          window.dispatchEvent(new CustomEvent('pageChange', { detail: { page: 'menu', menuIndex: 0 } }));
          onStart();
        }, 1800);
      } else {
        setLoginError(data.error || 'Une erreur est survenue');
      }
    } catch (e) {
      setLoginError('Erreur de connexion au serveur');
    }
  };

  const handleLogin = async () => {
    if (!loginEmail || !form.password) return;
    try {
      const res = await fetch(`${API}/connexion.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: form.password }),
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('foxaya_user', JSON.stringify({ ...data.user, lastLogin: Date.now() }));
        setSubmitted(true);
        setTimeout(() => {
          setShowSignup(false);
          window.dispatchEvent(new CustomEvent('pageChange', { detail: { page: 'menu', menuIndex: 0 } }));
          onStart();
        }, 1500);
      } else {
        setLoginError(data.error || 'Email ou mot de passe incorrect');
      }
    } catch (e) {
      setLoginError('Erreur de connexion au serveur');
    }
  };

  const handleJeMeLance = () => {
    const stored = localStorage.getItem('foxaya_user');
    if (stored) {
      const user = JSON.parse(stored);
      const daysSince = (Date.now() - (user.lastLogin || user.createdAt)) / (1000 * 60 * 60 * 24);
      if (daysSince < 30) {
        window.dispatchEvent(new CustomEvent('pageChange', { detail: { page: 'menu', menuIndex: 0 } }));
        onStart();
        return;
      }
      // Plus de 30 jours → reconnexion
      setMode('login');
    }
    setShowSignup(true);
  };

  useEffect(() => {
    const handleResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (showPrototypeNotice) return;
    const t1 = setTimeout(() => setShowFox(true), 500);
    let alreadySeen = false;
    try { alreadySeen = localStorage.getItem(FOX_BUBBLE_SEEN_KEY) === '1'; } catch {}
    const t2 = !alreadySeen ? setTimeout(() => {
      setShowFoxBubble(true);
      try { localStorage.setItem(FOX_BUBBLE_SEEN_KEY, '1'); } catch {}
    }, 900) : null;
    const t3 = !alreadySeen ? setTimeout(() => setShowFoxBubble(false), 8000) : null;
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [showPrototypeNotice]);

  return (
    <>
      <CookieConsent />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        body { background: #001c2b; overflow-x: hidden; }

        @keyframes pulseScale {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @keyframes lineGrow {
          to { transform: scaleX(1); }
        }

        @keyframes arrowBounce {
          0%   { transform: translateX(0); }
          40%  { transform: translateX(5px); }
          70%  { transform: translateX(-2px); }
          100% { transform: translateX(0); }
        }

        .hero-btn-desktop:hover {
          box-shadow: 0 0 0 3px rgba(0,165,178,0.25), 0 8px 32px rgba(0,165,178,0.35) !important;
        }

        .hero-btn-desktop:hover::before {
          transform: translateX(100%) !important;
          transition: transform 0.5s ease !important;
        }

        .hero-btn-desktop:hover .arrow-inner {
          animation: arrowBounce 0.4s ease;
        }

        .stat-row-item:hover {
          background: rgba(255,255,255,0.08) !important;
          border-color: rgba(29,200,160,0.3) !important;
        }

        @keyframes foxSlideInRight {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes foxBubbleInLeft {
          from { opacity: 0; transform: translateX(8px) scale(0.92); }
          to   { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes foxIdle {
          0%, 100% { transform: translateY(0) rotate(-4deg); }
          50% { transform: translateY(-5px) rotate(-1deg); }
        }
      `}
      </style>

      {/* ── POPUP AVERTISSEMENT PROTOTYPE ── */}
      {showPrototypeNotice && (
        <>
          <div style={{
            position: "fixed", inset: 0, zIndex: 100000,
            background: "rgba(0,0,0,0.75)",
          }} />
          <div style={{
            position: "fixed", top: "50%", left: "50%",
            transform: "translate(-50%,-50%)",
            zIndex: 100001,
            width: "90vw", maxWidth: 420,
            background: "#fff",
            borderRadius: 24,
            padding: "2rem",
            fontFamily: "'Montserrat', sans-serif",
            boxShadow: "0 32px 80px rgba(0,0,0,0.5)",
            textAlign: "center",
          }}>
            <div style={{ marginBottom: 14, display: "flex", justifyContent: "center" }}>
              <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#eaf6f6", border: "1.5px solid rgba(0,165,178,0.25)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#00A5B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="13" />
                  <line x1="12" y1="16.5" x2="12.01" y2="16.5" />
                </svg>
              </div>
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#001c2b", marginBottom: 8 }}>
              Ce site est un prototype
            </div>
            <div style={{ fontSize: 13.5, color: "#666", lineHeight: 1.6, marginBottom: 22 }}>
              Vous consultez une version de démonstration, encore en cours de développement. Certains contenus ou fonctionnalités peuvent être erronés, incomplets, provisoires ou instables.
            </div>
            <button onClick={() => setShowPrototypeNotice(false)} style={{
              width: "100%", padding: "13px",
              background: "linear-gradient(135deg,#00A5B2,#9CD14D)",
              color: "#fff",
              border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700,
              fontFamily: "'Montserrat', sans-serif",
              cursor: "pointer",
              transition: "all 0.2s",
            }}>
              OK
            </button>
          </div>
        </>
      )}

      {/* ── RENARD — mascotte du site ──
          Sort du bord droit de l'écran, à moitié hors cadre, comme s'il
          grimpait pour observer. Reste discret : juste lui + sa bulle, qui
          se referme seule. */}
      {showFox && (
        <div style={{
          position: "fixed", right: mobile ? -2 : -2, top: mobile ? "auto" : "34%",
          bottom: mobile ? 90 : "auto",
          zIndex: 9990, display: "flex", alignItems: "center", gap: 8,
          animation: "foxSlideInRight 0.6s cubic-bezier(.22,1,.36,1) both",
          maxWidth: mobile ? "calc(100vw - 20px)" : "none",
        }}>
          {showFoxBubble && (
            <div style={{
              position: "relative",
              maxWidth: mobile ? 168 : 240,
              background: "#fff", color: "#001c2b",
              borderRadius: "14px 14px 2px 14px",
              padding: mobile ? "10px 12px" : "12px 14px",
              fontFamily: "'Montserrat', sans-serif",
              fontSize: mobile ? 11.5 : 13,
              lineHeight: 1.45,
              boxShadow: "0 10px 28px rgba(0,0,0,0.35)",
              animation: "foxBubbleInLeft 0.35s ease both",
            }}>
              <button
                onClick={() => setShowFoxBubble(false)}
                aria-label="Fermer"
                style={{
                  position: "absolute", top: 4, right: 6,
                  background: "transparent", border: "none",
                  color: "rgba(0,0,0,0.35)", fontSize: 14, lineHeight: 1,
                  cursor: "pointer", padding: 4,
                }}
              >✕</button>
              <strong>Salut, moi c'est Foxy ,</strong><br />
              Bienvenue sur ma plateforme, n'hésite pas à venir me voir pour quelques conseils.
            </div>
          )}
          <img
            src={FOX_SIDE_IMG_URL}
            alt="Foxy, votre guide"
            role="button"
            tabIndex={0}
            aria-label={showFoxBubble ? "Fermer la bulle de Foxy" : "Afficher la bulle de Foxy"}
            onClick={() => {
              setShowFoxBubble(value => !value);
              try { localStorage.setItem(FOX_BUBBLE_SEEN_KEY, '1'); } catch {}
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setShowFoxBubble(value => !value);
                try { localStorage.setItem(FOX_BUBBLE_SEEN_KEY, '1'); } catch {}
              }
            }}
            style={{
              width: mobile ? 58 : 100, height: "auto", display: "block",
              cursor: "pointer", flexShrink: 0,
              animation: "foxIdle 4.5s ease-in-out infinite",
              filter: "drop-shadow(0 6px 14px rgba(0,0,0,0.35))",
            }}
          />
        </div>
      )}

      <section style={{
        minHeight: "100vh",
        background: "#001c2b",
        fontFamily: "'Montserrat', sans-serif",
        position: "relative",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        paddingTop: 10,
        isolation: "auto",
      }}>

        {/* ── Orbs ── */}
        <div style={{
          position: "fixed", borderRadius: "50%", pointerEvents: "none",
          width: 500, height: 500, background: "#fff",
          top: -200, right: -150, opacity: 0.02,
          animation: "pulseScale 7s ease-in-out infinite",
        }} />
        <div style={{
          position: "fixed", borderRadius: "50%", pointerEvents: "none",
          width: 400, height: 400, background: "#fff",
          top: "calc(50% - 200px)", left: -180, opacity: 0.02,
          animation: "pulseScale 9s 2s ease-in-out infinite",
        }} />
        <div style={{
          position: "fixed", borderRadius: "50%", pointerEvents: "none",
          width: 350, height: 350, background: "#fff",
          bottom: -150, right: -120, opacity: 0.02,
          animation: "pulseScale 8s 4s ease-in-out infinite",
        }} />

        {/* ── Decorative dots ── */}
        <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 1 }}
          viewBox="0 0 1200 800" fill="none" preserveAspectRatio="xMidYMid slice">
          {DOTS.map(([cx, cy, r], i) => (
            <circle key={i} cx={cx} cy={cy} r={r} fill="#ffffff" opacity="0.1" />
          ))}
          <path d="M-30 620 Q300 460 600 530 Q840 590 1230 420"
            stroke="#00A5B2" strokeWidth="0.7" strokeDasharray="6 12" opacity="0.12" />
        </svg>

        {/* ── Inner content ── */}
        <div style={{
          position: "relative", zIndex: 2,
          width: "100%",
          display: "flex", flexDirection: "column",
          alignItems: "center", textAlign: "center",
          padding: mobile ? "4rem 2.5rem" : "6rem 4%",
        }}>

          {/* ── Title MOBILE ── */}
          {mobile && (
            <h1 style={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600, color: "#fff",
              lineHeight: 1.2, fontSize: 35,
              textAlign: "left", marginBottom: "3rem",
              animation: "fadeUp 0.7s ease both",
            }}>
              Comment entreprendre aujourd'hui tout en respectant{" "}
              <em style={{
                fontStyle: "normal", color: "#9fc131",
                position: "relative", display: "inline-block",
              }}>
                les enjeux
                <span style={{
                  position: "absolute", left: 0, bottom: -2,
                  width: "100%", height: 3,
                  background: "#9fc131", borderRadius: 2,
                  transform: "scaleX(0)", transformOrigin: "left",
                  animation: "lineGrow 0.6s 0.8s ease forwards",
                  display: "block",
                }} />
              </em>{" "}de notre époque ?
            </h1>
          )}

          {/* ── Title DESKTOP ── */}
          {!mobile && (
            <h1 style={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600, color: "#fff",
              lineHeight: 1.2,
              fontSize: "clamp(36px, 3.5vw, 60px)",
              maxWidth: "100%", marginBottom: "3.2rem",
              animation: "fadeUp 0.7s ease both",
            }}>
              Comment entreprendre aujourd'hui,<br />
              tout en respectant<br />
              <em style={{
                fontStyle: "normal", color: "#9fc131",
                position: "relative", display: "inline-block",
              }}>
                les enjeux
                <span style={{
                  position: "absolute", left: 0, bottom: -2,
                  width: "100%", height: 3,
                  background: "#9fc131", borderRadius: 2,
                  transform: "scaleX(0)", transformOrigin: "left",
                  animation: "lineGrow 0.6s 0.8s ease forwards",
                  display: "block",
                }} />
              </em>{" "}de notre époque ?
            </h1>
          )}

          {/* ── CTA Button ── */}
          <button
            className={mobile ? "" : "hero-btn-desktop"}
            onClick={handleJeMeLance}
            style={{
              display: "inline-flex", alignItems: "center", gap: 10,
              background: "#00a5b2", color: "#fff",
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600, letterSpacing: "0.06em",
              border: "none", borderRadius: 999, cursor: "pointer",
              animation: "fadeUp 0.7s 0.15s ease both",
              transition: "background 0.15s, transform 0.15s, box-shadow 0.15s",
              textDecoration: "none", position: "relative",
              fontSize: mobile ? 15 : 20,
              padding: mobile ? "13px 26px" : "17px 40px",
              marginBottom: mobile ? "3.2rem" : "4.5rem",
              overflow: "hidden",
            }}
          >
            Je me lance
            <div
              className="arrow-inner"
              style={{
                background: "rgba(10,46,42,0.2)", borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                width: mobile ? 20 : 28, height: mobile ? 20 : 28,
              }}
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path d="M2.5 6.5h8M7.5 3.5l3 3-3 3" stroke="#ffffff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </button>

          {/* ── POPUP INSCRIPTION / CONNEXION ── */}
          {showSignup && (
            <>
              <div
                onClick={() => setShowSignup(false)}
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 99998,
                  background: mobile ? "rgba(0,0,0,0.58)" : "rgba(0,0,0,0.75)",
                  backdropFilter: mobile ? "blur(2px)" : "none",
                  WebkitBackdropFilter: mobile ? "blur(2px)" : "none",
                }}
              />
              <div style={{
                position: "fixed",
                zIndex: 99999,
                ...(mobile
                  ? {
                      left: 10,
                      right: 10,
                      bottom: 10,
                      top: "auto",
                      transform: "none",
                      width: "auto",
                      maxWidth: "none",
                      maxHeight: "calc(100dvh - 20px)",
                      overflowY: "auto",
                      overscrollBehavior: "contain",
                      WebkitOverflowScrolling: "touch",
                      borderRadius: 20,
                      padding: "1rem 1rem calc(1rem + env(safe-area-inset-bottom))",
                    }
                  : {
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%,-50%)",
                      width: "90vw",
                      maxWidth: 460,
                      maxHeight: "90vh",
                      overflowY: "auto",
                      borderRadius: 24,
                      padding: "2rem",
                    }),
                background: "#fff",
                fontFamily: "'Montserrat', sans-serif",
                boxShadow: mobile
                  ? "0 -12px 40px rgba(0,0,0,0.28)"
                  : "0 32px 80px rgba(0,0,0,0.5)",
              }}>
                <button
                  type="button"
                  onClick={() => setShowSignup(false)}
                  aria-label="Fermer la fenêtre"
                  style={{
                    position: "sticky",
                    top: 0,
                    float: "right",
                    zIndex: 4,
                    width: mobile ? 38 : 34,
                    height: mobile ? 38 : 34,
                    margin: mobile ? "-4px -4px 2px 10px" : "-8px -8px 0 10px",
                    border: "1px solid rgba(0,28,43,0.10)",
                    borderRadius: "50%",
                    background: "#f4f7f7",
                    color: "#001c2b",
                    fontSize: mobile ? 24 : 21,
                    lineHeight: 1,
                    display: "grid",
                    placeItems: "center",
                    cursor: "pointer",
                    boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
                  }}
                >
                  ×
                </button>
                {!submitted ? (
                  <>
                    {/* Header */}
                    <div style={{ textAlign: "center", marginBottom: mobile ? "0.8rem" : "1.4rem", clear: "both" }}>
                      <div style={{ marginBottom: 10, display: "flex", justifyContent: "center" }}>
                        <div style={{ width: mobile ? 46 : 64, height: mobile ? 46 : 64, borderRadius: "50%", background: "#eaf6f6", border: "1.5px solid rgba(0,165,178,0.25)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <svg width={mobile ? 24 : 32} height={mobile ? 24 : 32} viewBox="0 0 24 24" fill="none" stroke="#00A5B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z"/>
                            <path d="M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/>
                          </svg>
                        </div>
                      </div>
                      <div style={{ fontSize: mobile ? 16 : 18, fontWeight: 800, color: "#001c2b", marginBottom: 4, padding: mobile ? "0 34px" : 0 }}>
                        {mode === 'signup' ? 'Bienvenue sur Foxaya' : 'Bon retour !'}
                      </div>
                      <div style={{ fontSize: mobile ? 11.5 : 13, color: "#888", lineHeight: 1.45, padding: mobile ? "0 12px" : 0 }}>
                        {mode === 'signup'
                          ? 'Créez votre profil pour accéder aux missions RSE'
                          : 'Connectez-vous pour retrouver votre progression'}
                      </div>
                    </div>

                    {/* Tabs */}
                    <div style={{
                      display: "flex", background: "#f4f4f2", borderRadius: 12,
                      padding: 4, marginBottom: mobile ? "0.9rem" : "1.4rem", gap: 4,
                    }}>
                      {['signup', 'login'].map(m => (
                        <button key={m} onClick={() => { setMode(m); setLoginError(''); }} style={{
                          flex: 1, padding: mobile ? "8px 6px" : "9px",
                          background: mode === m ? "#fff" : "transparent",
                          border: "none", borderRadius: 9,
                          fontSize: mobile ? 11.5 : 13, fontWeight: mode === m ? 700 : 500,
                          color: mode === m ? "#001c2b" : "#888",
                          cursor: "pointer",
                          fontFamily: "'Montserrat', sans-serif",
                          boxShadow: mode === m ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                          transition: "all 0.2s",
                        }}>
                        {m === 'signup' ? (
                          <span style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
                            Inscription gratuite
                          </span>
                        ) : (
                          <span style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                            J'ai déjà un compte
                          </span>
                        )}
                        </button>
                      ))}
                    </div>

                    {/* FORMULAIRE INSCRIPTION */}
                    {mode === 'signup' && (
                      <>
                        <div style={{ marginBottom: mobile ? 8 : 12 }}>
                          <label style={{ fontSize: 11, fontWeight: 700, color: "#001c2b", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 6 }}>Prénom</label>
                          <input type="text" placeholder="Votre prénom" value={form.prenom}
                            onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))}
                            style={{ width: "100%", padding: mobile ? "9px 11px" : "11px 14px", border: "1.5px solid #e8e8e6", borderRadius: 10, fontSize: mobile ? 13 : 14, fontFamily: "'Montserrat', sans-serif", outline: "none", boxSizing: "border-box", color: "#001c2b" }}
                          />
                        </div>
                        <div style={{ marginBottom: mobile ? 8 : 12 }}>
                          <label style={{ fontSize: 11, fontWeight: 700, color: "#001c2b", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 6 }}>Email</label>
                          <input type="email" placeholder="votre@email.com" value={form.email}
                            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                            style={{ width: "100%", padding: mobile ? "9px 11px" : "11px 14px", border: "1.5px solid #e8e8e6", borderRadius: 10, fontSize: mobile ? 13 : 14, fontFamily: "'Montserrat', sans-serif", outline: "none", boxSizing: "border-box", color: "#001c2b" }}
                          />
                        </div>
                        <div style={{ marginBottom: mobile ? 8 : 12 }}>
                          <label style={{ fontSize: 11, fontWeight: 700, color: "#001c2b", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 6 }}>Secteur d'activité</label>
                          <select value={form.secteur} onChange={e => setForm(f => ({ ...f, secteur: e.target.value }))}
                            style={{ width: "100%", padding: mobile ? "9px 11px" : "11px 14px", border: "1.5px solid #e8e8e6", borderRadius: 10, fontSize: mobile ? 13 : 14, fontFamily: "'Montserrat', sans-serif", outline: "none", boxSizing: "border-box", color: form.secteur ? "#001c2b" : "#aaa", background: "#fff" }}
                          >
                            <option value="">Choisissez votre secteur</option>
                            {SECTEURS.map((s, i) => <option key={i} value={s.value}>{s.label}</option>)}
                          </select>
                        </div>
                        <div style={{ marginBottom: mobile ? 12 : 20 }}>
                          <label style={{ fontSize: 11, fontWeight: 700, color: "#001c2b", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 6 }}>Mot de passe</label>
                          <input type="password" placeholder="Créez votre mot de passe"
                            value={form.password || ''}
                            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                            style={{ width: "100%", padding: mobile ? "9px 11px" : "11px 14px", border: "1.5px solid #e8e8e6", borderRadius: 10, fontSize: mobile ? 13 : 14, fontFamily: "'Montserrat', sans-serif", outline: "none", boxSizing: "border-box", color: "#001c2b" }}
                          />
                        </div>
                        <button onClick={handleSignup} style={{
                          width: "100%", padding: mobile ? "11px" : "13px",
                          background: form.prenom && form.email && form.secteur && form.password ? "linear-gradient(135deg,#00A5B2,#9CD14D)" : "#e8e8e6",
                          color: form.prenom && form.email && form.secteur && form.password ? "#fff" : "#aaa",
                          border: "none", borderRadius: 12, fontSize: mobile ? 13.5 : 15, fontWeight: 700,
                          fontFamily: "'Montserrat', sans-serif",
                          cursor: form.prenom && form.email && form.secteur && form.password ? "pointer" : "default",
                          transition: "all 0.2s",
                        }}>
                          Accéder aux missions →
                        </button>
                        <div style={{ textAlign: "center", marginTop: 10, fontSize: 11, color: "#bbb" }}>
                          Gratuit • Pas de spam • Désabonnement en 1 clic
                        </div>
                        <div style={{ textAlign: "center", marginTop: mobile ? 8 : 12 }}>
                          <button onClick={() => {
                            setShowSignup(false);
                            window.dispatchEvent(new CustomEvent('pageChange', { detail: { page: 'menu', menuIndex: 0 } }));
                            onStart();
                          }} style={{
                            width: mobile ? "100%" : "auto",
                            minHeight: mobile ? 42 : "auto",
                            padding: mobile ? "9px 12px" : 0,
                            background: mobile ? "#f4f7f7" : "transparent",
                            border: mobile ? "1px solid #dfe8e9" : "none",
                            borderRadius: mobile ? 11 : 0,
                            fontSize: mobile ? 12.5 : 12,
                            fontWeight: mobile ? 700 : 400,
                            color: mobile ? "#365d67" : "#bbb",
                            cursor: "pointer",
                            fontFamily: "'Montserrat', sans-serif",
                            textDecoration: mobile ? "none" : "underline",
                          }}>
                            Continuer sans inscription
                          </button>
                        </div>
                      </>
                    )}

                    {/* FORMULAIRE CONNEXION */}
                    {mode === 'login' && (
                      <>
                        <div style={{ marginBottom: mobile ? 8 : 12 }}>
                          <label style={{ fontSize: 11, fontWeight: 700, color: "#001c2b", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 6 }}>Votre email</label>
                          <input type="email" placeholder="votre@email.com" value={loginEmail}
                            onChange={e => { setLoginEmail(e.target.value); setLoginError(''); }}
                            style={{ width: "100%", padding: mobile ? "9px 11px" : "11px 14px", border: `1.5px solid ${loginError ? '#ef4444' : '#e8e8e6'}`, borderRadius: 10, fontSize: mobile ? 13 : 14, fontFamily: "'Montserrat', sans-serif", outline: "none", boxSizing: "border-box", color: "#001c2b" }}
                          />
                          {loginError && <div style={{ fontSize: 12, color: "#ef4444", marginTop: 6 }}>{loginError}</div>}
                        </div>
                        <div style={{ marginBottom: mobile ? 12 : 20 }}>
                          <label style={{ fontSize: 11, fontWeight: 700, color: "#001c2b", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 6 }}>Mot de passe</label>
                          <input type="password" placeholder="Votre mot de passe"
                            value={form.password || ''}
                            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                            style={{ width: "100%", padding: mobile ? "9px 11px" : "11px 14px", border: "1.5px solid #e8e8e6", borderRadius: 10, fontSize: mobile ? 13 : 14, fontFamily: "'Montserrat', sans-serif", outline: "none", boxSizing: "border-box", color: "#001c2b" }}
                          />
                        </div>
                        <button onClick={handleLogin} style={{
                          width: "100%", padding: mobile ? "11px" : "13px",
                          background: loginEmail ? "linear-gradient(135deg,#00A5B2,#9CD14D)" : "#e8e8e6",
                          color: loginEmail ? "#fff" : "#aaa",
                          border: "none", borderRadius: 12, fontSize: mobile ? 13.5 : 15, fontWeight: 700,
                          fontFamily: "'Montserrat', sans-serif",
                          cursor: loginEmail ? "pointer" : "default",
                          transition: "all 0.2s",
                        }}>
                          Me connecter →
                        </button>
                        <div style={{ textAlign: "center", marginTop: 12 }}>
                          <button onClick={() => setMode('signup')} style={{ background: "transparent", border: "none", fontSize: 12, color: "#00A5B2", cursor: "pointer", fontFamily: "'Montserrat', sans-serif" }}>
                            Pas encore de compte ? S'inscrire gratuitement
                          </button>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div style={{ textAlign: "center", padding: mobile ? "0.6rem 0" : "1rem 0", clear: "both" }}>
                    <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}>
                      <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#eaf6f6", border: "1.5px solid rgba(0,165,178,0.25)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#00A5B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                          <polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                      </div>
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#001c2b", marginBottom: 8 }}>
                      {mode === 'login' ? 'Ravi de vous revoir !' : `Bienvenue ${form.prenom} !`}
                    </div>
                    <div style={{ fontSize: 13, color: "#888", lineHeight: 1.6 }}>
                      {mode === 'login' ? 'Reconnexion réussie. Vous allez être redirigé...' : 'Votre profil est créé. Vous allez être redirigé vers vos missions...'}
                    </div>
                    <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
                      <div style={{ width: 40, height: 4, borderRadius: 2, background: "linear-gradient(90deg,#00A5B2,#9CD14D)", animation: "lineGrow 1.8s ease forwards" }} />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Quote — mobile only ── */}
          {mobile && (
            <div style={{
              borderLeft: "2px solid #9fc131",
              paddingLeft: 16, textAlign: "left",
              marginBottom: "3rem",
              animation: "fadeUp 0.7s 0.25s ease both",
            }}>
              <p style={{ fontSize: 15, fontWeight: 400, fontStyle: "italic", color: "rgba(255,255,255,0.7)", lineHeight: 1.3 }}>
                "Le meilleur moment pour planter un arbre, c'était il y a 20 ans. Le deuxième meilleur moment, c'est maintenant."
              </p>
              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", letterSpacing: "0.1em", marginTop: 6 }}>
                — Proverbe chinois
              </p>
            </div>
          )}

          {/* ── Separator — desktop only ── */}
          {!mobile && (
            <div style={{
              width: "100%", maxWidth: 900, height: 1,
              background: "rgba(255,255,255,0.07)",
              marginBottom: "3.5rem",
              animation: "fadeUp 0.7s 0.3s ease both",
            }} />
          )}

          {/* ── Stats MOBILE ── */}
          {mobile && (
            <div style={{
              display: "flex", flexDirection: "column", gap: 10,
              width: "100%",
              animation: "fadeUp 0.7s 0.35s ease both",
            }}>
              {[
                { num: "×2,3", label: "Les entreprises à mission croissent 2,3 fois plus vite que les autres.", source: "McKinsey & Jump Associates" },
                { num: "+58%", label: "Les entreprises engagées gagnent 58% de chiffre d'affaires en plus.", source: "Harvard Business Review" },
                { num: "70%", label: "des consommateurs prêts à payer plus pour une marque responsable.", source: "McKinsey - étude multi-secteurs" },
              ].map((s, i) => (
                <div key={i} className="stat-row-item" style={{
                  display: "flex", alignItems: "center", gap: 14,
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(29,200,160,0.15)",
                  borderRadius: 14, padding: "14px 16px",
                  transition: "background 0.2s, border-color 0.2s",
                }}>
                  <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 600, color: "#9fc131", lineHeight: 1, fontSize: 24, minWidth: 62, flexShrink: 0 }}>
                    {s.num}
                  </div>
                  <div>
                    <div style={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.5, fontSize: 15, marginBottom: 5, textAlign: "left" }}>{s.label}</div>
                    <div style={{ color: "rgba(255,255,255,0.45)", letterSpacing: "0.06em", fontSize: 10, textAlign: "left" }}>{s.source}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Stats DESKTOP ── */}
          {!mobile && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1px 1fr 1px 1fr",
              maxWidth: 900, width: "100%",
              animation: "fadeUp 0.7s 0.35s ease both",
            }}>
              {[
                { num: "×2,3", label: "Les entreprises à mission croissent 2,3 fois plus vite que les autres", source: "McKinsey & Jump Associates" },
                null,
                { num: "+58%", label: "Les entreprises engagées gagnent 58% de chiffre d'affaires en plus.", source: "Harvard Business Review" },
                null,
                { num: "70%", label: "des consommateurs prêts à payer plus pour une marque responsable", source: "McKinsey — étude multi-secteurs" },
              ].map((s, i) =>
                s === null ? (
                  <div key={i} style={{ background: "rgba(255,255,255,0.08)", alignSelf: "stretch" }} />
                ) : (
                  <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "0 2.5rem" }}>
                    <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 600, color: "#9fc131", lineHeight: 1, fontSize: 50 }}>{s.num}</div>
                    <div style={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.5, fontSize: 15, maxWidth: 180, textAlign: "center" }}>{s.label}</div>
                    <div style={{ color: "rgba(255,255,255,0.45)", letterSpacing: "0.06em", fontSize: 10.5, textAlign: "center" }}>{s.source}</div>
                  </div>
                )
              )}
            </div>
          )}

        </div>
      </section>
    </>
  );
}